// Import the necessary components
import VerofreshRecord from './VerofreshRecord'
import { findIndex, each, partition } from 'lodash'
import Moment from 'moment'

class RouteRecord extends VerofreshRecord {
  /**
   * Returns the route product matching the product id specified. If no route
   * product with the product specified exists returns undefined.
   *
   * @param Integer productId
   * @return RouteProductRecord|undefined
   */
  getRouteProduct (productId) {
    const products = this.route_products.filter(routeProduct => routeProduct.product_id === productId)
    if (products.length > 0) {
      return products[0]
    }
    return undefined
  }

  /**
   * Takes a route product record and adds to or updates the route_products
   * member attribute depending on if the route product is already in the array
   *
   * @param RouteProductRecord routeProduct
   */
  pushRouteProduct (routeProduct) {
    if (this.route_products === undefined) {
      this.route_products = [routeProduct]
    }

    const idx = findIndex(this.route_products, existingRouteProduct => {
      return existingRouteProduct.id === routeProduct.id
    })

    if (idx > -1) {
      this.route_products[idx] = routeProduct
    } else {
      this.route_products.push(routeProduct)
    }
  }

  /**
   * Indicates whether or not the route has started. Will return undefined if
   * the start is undefined.
   *
   * @return boolean|undefined
   */
  get started () {
    if (this.start === undefined) {
      return undefined
    }

    return this.start === null || Moment(this.start).isBefore(Moment())
  }

  get ended () {
    if (this.end === undefined) {
      return undefined
    }

    if (this.end == null) return false

    return Moment(this.end).isBefore(Moment())
  }

  /**
   * Gets the route's duration as a Moment Duration object.
   * @returns Moment.Duration
   */
  get duration () {
    const start = Moment(this.start)
    let end = Moment(this.end)

    if (start.isBefore() && !end.isValid()) {
      end = Moment()
    }

    if (!start.isValid() || !end.isValid()) {
      return undefined
    }

    return Moment.duration(end.diff(start))
  }

  /**
   * Utility method to make it easier to fetch route products on this route by
   * the product they describe. The function name below is intentionally snake
   * cased to match other parameters
   */
  // eslint-disable-next-line
  get product_indexed_route_products () {
    const indexedRouteProducts = {}

    if (this.route_products === undefined) {
      return indexedRouteProducts
    }

    const routeProducts = partition(this.route_products, x => x.isSoftDeleted())
    // Iterate over the partitioned results - the deleted route products will come first and then be overwritten with
    // any non-deleted ones.
    each(routeProducts, rpa => {
      each(rpa, rp => {
        indexedRouteProducts[rp.product_id] = rp
      })
    })
    return indexedRouteProducts
  }

  /**
   * Util method to group route products into their respective product zones.
   * Similar to product_indexed_route_products, but grouped by zone
   * Requires that the products relation be loaded as well.
   */
  // eslint-disable-next-line
  get zone_indexed_route_products () {
    const zoneRouteProducts = {}

    if (this.route_products === undefined) {
      return zoneRouteProducts
    }

    this.route_products.forEach(routeProduct => {
      if (routeProduct.product) {
        const zone = routeProduct.product.zone
        if (zoneRouteProducts[zone] === undefined) {
          zoneRouteProducts[zone] = []
        }
        zoneRouteProducts[zone].push(routeProduct)
      }
    })

    return zoneRouteProducts
  }

  fetchRouteProducts () {
    return this._mapper()
      .datastore
      .getMapper('Route')
      .fetchRouteProducts({
        id: this.id
      })
  }

  createRouteProduct (routeProduct) {
    return this._mapper()
      .datastore
      .getMapper('Route')
      .createRouteProduct({
        id: this.id,
        data: routeProduct
      })
  }

  deleteRouteProduct (routeProduct) {
    return this._mapper()
      .datastore
      .getMapper('Route')
      .deleteRouteProduct({
        product_id: routeProduct.product_id,
        id: this.id
      })
  }

  restoreRouteProduct (routeProduct) {
    return this._mapper()
      .datastore
      .getMapper('Route')
      .updateRouteProduct({
        product_id: routeProduct.product_id,
        id: this.id,
        data: { deleted: null }
      })
  }

  updateRouteProduct (routeProduct) {
    return this._mapper()
      .datastore
      .getMapper('Route')
      .updateRouteProduct({
        product_id: routeProduct.product_id,
        id: this.id,
        data: routeProduct
      })
  }
}

const relations = {
  hasMany: {
    RouteProduct: {
      foreignKey: 'route_id',
      localField: 'route_products'
    },
    RouteLocation: {
      foreignKey: 'route_id',
      localField: 'route_locations'
    },
    RouteViolation: {
      foreignKey: 'route_id',
      localField: 'route_violations'
    }
  },
  belongsTo: {
    Vehicle: {
      foreignKey: 'vehicle_id',
      localField: 'vehicle'
    },
    RoutePlan: {
      foreignKey: 'route_plan_id',
      localField: 'route_plan'
    },
    Customer: {
      foreignKey: 'customer_id',
      localField: 'customer'
    }
  }
}

// Actions allow us to defined additional calls we can make to related
// endpoints.
const actions = {
  fetchRouteProducts: {
    method: 'GET',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/product?expand=product`
    },
    response (response) {
      return response.data
    }
  },
  createRouteProduct: {
    method: 'POST',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/product`
    },
    response (response) {
      return response.data
    }
  },
  deleteRouteProduct: {
    method: 'DELETE',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/product/${options.product_id}`
    },
    response (response) {
      return response.data
    }
  },
  updateRouteProduct: {
    method: 'PATCH',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/product/${options.product_id}`
    },
    response (response) {
      return response.data
    }
  }
}

export default RouteRecord
export { relations, actions }
